import { InspectionImages } from "./asset";

export type Inspection = {
    id: string;
    vesselName: string | null;
    vesselImo: string | null;
    inspectionTypeId: number | null;
    inspectionType: string | null;
    /** ASIMS `InspectionType.ShortName` (e.g. `IDCI`). Optional because
     *  config-driven inspections have no ASIMS type row. Prefer it over
     *  `inspectionTypeId`, whose values differ between environments. */
    inspectionTypeShortName?: string | null;
    inspectionNumber: string | null;
    inspectionDateFrom: string | null;
    inspectionDateTo: string | null;
    inspectionReport: InspectionElement[] | null;
    orderedDate: string | null
    issuedAt: string | null;
    reportDate: string | null;
    status: string | null;
    port: string | null;
    country: string | null;
    recommendedPrice: string | null;
    quoteLink: string | null;
    earlyRelease: boolean | null;
    vesselType: string | null;
    overallScore: number | null;
    managementScore: number | null;
    conditionScore: number | null;
    totalPrice: number | null;
    enableLowPriorityDefects: boolean | null;
    grades: Grade[] | null;
    /** Photos captured during the inspection, grouped by question (asset-manager). */
    images?: InspectionImages | null;
    /** Processing path: 'config-driven' inspections keep their
     *  reviewer-editable answers in the answers table, keyed by questionId. */
    pipeline?: string | null;
    /** Checklist allocation from the inspect-backend record — absent on
     *  inspections that predate the config-driven pipeline. */
    checklistType?: string | null;
    checklistVersion?: string | null;
    [key: string]: unknown;
}

export enum InspectionStatus {
    NEW = "NEW",
    BIDDING = "BIDDING",
    UPCOMING = "UPCOMING",
    CONFIRMED = "CONFIRMED",
    INSPECTION_COMPLETED = "INSPECTION_COMPLETED",
    FULL_REPORT = "FULL_REPORT",
    CANCELLED = "CANCELLED",
    COMMENCED = "COMMENCED",
    HOLD = "HOLD"
}

export type InspectionElement = {
    AvailableForCustomer: boolean;
    ElementType: number;
    Id: number;
    InspectionId: string;
    Released: boolean;
    Name: string;
    SortOrder: number;
}

export type Grade = {
    id: number;
    inspectionId: string;
    vesselGradeId: number;
    type: number;
    name: string;
    weight: number;
    score: number;
    point: number;
    adjustedPoint?: number;
    /**
     * Display position; not the vesselGradeId order. Optional because the
     * get-by-id payload has not always carried it, and a version that omits it
     * cannot be told apart from one that sends it at the call site.
     */
    order?: number;
}

export type AdjustedGrade = {
    vesselGradeId: number;
    gradeValue: number;
}

/**
 * A single section grade in a save payload for the Grading page (A2-9130).
 * `point` is on the stored 0–10 scale (the UI 0–100 value divided by 10), or
 * null when the section is ungraded.
 */
export type SectionGradeUpdate = {
    vesselGradeId: number;
    point: number | null;
}

/**
 * A gradeable section returned by GET /inspections/{id}/grading-sections — the
 * template of sections for the inspection's type, before any grade is entered.
 */
export type GradingSection = {
    vesselGradeId: number;
    type: number;
    name: string;
    weight: number;
    order: number;
}

export type AdjustedGradeDto = {
    adjustedGrades: AdjustedGrade[];
    calculatedGrades: CalculateGrades;
}

export type CalculateGrades = {
    managementGrade: ManagementGrade;
    conditionGrade: ConditionGrade;
    overallGrade: number;
}

export type ConditionGrade = {
    totalConditionGrade: number;
    maxConditionGrade: number;
    computedConditionGrade: number;
}

export type ManagementGrade = {
    totalManagementGrade: number;
    maxManagementGrade: number;
    computedManagementGrade: number;
}

export type AiSummary = {
    sectionId: string;
    augmentedText: string;
    originalText: string;
}